import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { CustomizeModal } from "../components/product/CustomizeModal";
import { fetchCustomizationCatalog } from "../services/customization";
import type { CustomizationCatalog, Extra, Product, ProductId, ProductOptions } from "../types";
import { useCart } from "./CartContext";

type CatalogStatus = "loading" | "ready" | "error";

interface CustomizerValue {
  status: CatalogStatus;
  /** "Monte seu Lanche" (null se desligado, sem ingrediente obrigatório em estoque ou API antiga). */
  builder: CustomizationCatalog["builder"];
  /** Opções do lanche, ou null se ele não tem ingredientes/adicionais cadastrados. */
  optionsFor: (productId: ProductId) => ProductOptions | null;
  openCustomizer: (product: Product) => void;
  /** Lanche personalizável abre a personalização; o resto vai direto para o carrinho. */
  addOrCustomize: (product: Product) => void;
}

const CustomizerContext = createContext<CustomizerValue | null>(null);

interface Session {
  key: number;
  product: Product;
  options: ProductOptions;
}

export const CustomizerProvider = ({ children }: { children: ReactNode }) => {
  const { addItem } = useCart();
  const [catalog, setCatalog] = useState<CustomizationCatalog | null>(null);
  const [status, setStatus] = useState<CatalogStatus>("loading");
  const [session, setSession] = useState<Session | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let active = true;
    fetchCustomizationCatalog()
      .then((data) => {
        if (!active) return;
        setCatalog(data);
        setStatus("ready");
      })
      .catch(() => {
        // Sem catálogo (API antiga ou fora do ar), os lanches são adicionados direto, como antes.
        if (active) setStatus("error");
      });
    return () => {
      active = false;
    };
  }, []);

  const extrasById = useMemo(
    () => new Map<number, Extra>((catalog?.extras ?? []).map((extra) => [extra.id, extra])),
    [catalog],
  );

  const optionsFor = useCallback(
    (productId: ProductId): ProductOptions | null => {
      const entry = catalog?.products[String(productId)];
      if (!entry) return null;
      const allowed = new Set(entry.extras);
      const baseIds = new Set(entry.ingredients.map((i) => i.id));
      // O que já vem no lanche aparece na própria linha do ingrediente (com "+" se puder pedir a mais).
      const ingredients = entry.ingredients.map((i) => ({
        ...i,
        quantity: i.quantity ?? 1,
        extra: allowed.has(i.id) ? extrasById.get(i.id) ?? null : null,
      }));
      const extras = entry.extras.flatMap((id) => {
        const extra = extrasById.get(id);
        return extra && !baseIds.has(id) ? [extra] : [];
      });
      return ingredients.length > 0 || extras.length > 0 ? { ingredients, extras } : null;
    },
    [catalog, extrasById],
  );

  const openCustomizer = useCallback(
    (product: Product) => {
      const options = optionsFor(product.id);
      if (!options) return;
      setSession({ key: Date.now(), product, options });
      setOpen(true);
    },
    [optionsFor],
  );

  const addOrCustomize = useCallback(
    (product: Product) => {
      if (optionsFor(product.id)) openCustomizer(product);
      else addItem(product);
    },
    [optionsFor, openCustomizer, addItem],
  );

  const builder = catalog?.builder ?? null;
  const value = useMemo(
    () => ({ status, builder, optionsFor, openCustomizer, addOrCustomize }),
    [status, builder, optionsFor, openCustomizer, addOrCustomize],
  );

  return (
    <CustomizerContext.Provider value={value}>
      {children}
      <CustomizeModal
        open={open}
        sessionKey={session?.key ?? 0}
        product={session?.product ?? null}
        options={session?.options ?? null}
        onClose={() => setOpen(false)}
      />
    </CustomizerContext.Provider>
  );
};

export const useCustomizer = () => {
  const ctx = useContext(CustomizerContext);
  if (!ctx) throw new Error("useCustomizer precisa estar dentro de <CustomizerProvider>");
  return ctx;
};
